import { FunctionComponent, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { faTrash } from "@fortawesome/pro-regular-svg-icons";

import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";
import { Icon } from "@/components/Icon/Icon";
import { Toggle } from "@/components/Toggle/Toggle";
import { Tooltip } from "@/components/Tooltip/Tooltip";

import {
	useGetAvailableGroupsQuery,
	useGetPreferencesQuery,
	useUpsertPreferenceMutation,
	useDeletePreferenceMutation,
} from "@/pages/Private/redux/notification/notificationPreference.api";
import {
	NotificationFrequency,
	NotificationPreferenceGroup,
} from "@/pages/Private/pages/Notifications/enum/notification.enum";
import {
	NotificationGroupDefinition,
	NotificationPreference,
} from "@/pages/Private/pages/Notifications/schema/notification";

import { NotificationThresholds, withGroupDefaults } from "./notificationThresholds.util";
import { ReplyColleagueScopePicker } from "./ReplyColleagueScopePicker";

// ── Default preference values ────────────────────────────────────────
const DEFAULT_PREF: Pick<
	NotificationPreference,
	"appEnabled" | "emailEnabled" | "digestEnabled" | "frequency"
> = {
	appEnabled: true,
	emailEnabled: true,
	digestEnabled: false,
	frequency: NotificationFrequency.INSTANT,
};

export const NotificationPreferences: FunctionComponent = () => {
	const { t } = useTranslation();
	const tp = (key: string) => t(`notifications.preferences.${key}`);
	const { user } = useAppSelector(authSelector);

	const isActive = useIsFeatureActive(FeaturesCodes.NOTIFICATION_CENTER);

	const { data: groups, isLoading: isLoadingGroups } = useGetAvailableGroupsQuery(undefined, {
		skip: !isActive,
	});

	const { data: preferences, isLoading: isLoadingPrefs } = useGetPreferencesQuery(undefined, {
		skip: !isActive,
	});

	const [upsertPreference] = useUpsertPreferenceMutation();
	const [deletePreference] = useDeletePreferenceMutation();

	// Build a map of stored preferences keyed by group key (stored as eventType)
	const prefMap = useMemo(() => {
		const map: Record<string, NotificationPreference> = {};

		if (preferences) {
			for (const p of preferences) {
				map[p.eventType] = p;
			}
		}

		return map;
	}, [preferences]);

	const handleToggle = useCallback(
		async (
			groupKey: NotificationPreferenceGroup,
			field: "appEnabled" | "emailEnabled" | "digestEnabled",
			currentValue: boolean
		) => {
			try {
				const existing = prefMap[groupKey];
				const payload = {
					groupKey,
					appEnabled:
						field === "appEnabled"
							? !currentValue
							: (existing?.appEnabled ?? DEFAULT_PREF.appEnabled),
					emailEnabled:
						field === "emailEnabled"
							? !currentValue
							: (existing?.emailEnabled ?? DEFAULT_PREF.emailEnabled),
					digestEnabled:
						field === "digestEnabled"
							? !currentValue
							: (existing?.digestEnabled ?? DEFAULT_PREF.digestEnabled),
					frequency: existing?.frequency ?? DEFAULT_PREF.frequency,
					thresholds: existing?.thresholds ?? undefined,
				};

				await upsertPreference(payload);
			} catch (e) {
				console.error("Failed to update preference:", e);
			}
		},
		[prefMap, upsertPreference]
	);

	// PREC-3328: finer per-event toggles persisted in the preference row's `thresholds` JSON; the
	// colleague picker (PREC-4259) is stored beside them. A save writes every field as it is shown.
	const handleThresholdsChange = useCallback(
		async (group: NotificationGroupDefinition, patch: NotificationThresholds) => {
			try {
				const existing = prefMap[group.key];

				await upsertPreference({
					groupKey: group.key,
					appEnabled: existing?.appEnabled ?? DEFAULT_PREF.appEnabled,
					emailEnabled: existing?.emailEnabled ?? DEFAULT_PREF.emailEnabled,
					digestEnabled: existing?.digestEnabled ?? DEFAULT_PREF.digestEnabled,
					frequency: existing?.frequency ?? DEFAULT_PREF.frequency,
					thresholds: { ...withGroupDefaults(group, existing?.thresholds), ...patch },
				});
			} catch (e) {
				console.error("Failed to update sub-preference:", e);
			}
		},
		[prefMap, upsertPreference]
	);

	const handleSubToggle = useCallback(
		(group: NotificationGroupDefinition, subKey: string, currentValue: boolean) =>
			handleThresholdsChange(group, { [subKey]: !currentValue }),
		[handleThresholdsChange]
	);

	const handleFrequencyChange = useCallback(
		async (groupKey: NotificationPreferenceGroup, frequency: NotificationFrequency) => {
			try {
				const existing = prefMap[groupKey];
				const payload = {
					groupKey,
					appEnabled: existing?.appEnabled ?? DEFAULT_PREF.appEnabled,
					emailEnabled: existing?.emailEnabled ?? DEFAULT_PREF.emailEnabled,
					digestEnabled: existing?.digestEnabled ?? DEFAULT_PREF.digestEnabled,
					frequency,
					thresholds: existing?.thresholds ?? undefined,
				};

				await upsertPreference(payload);
			} catch (e) {
				console.error("Failed to update frequency:", e);
			}
		},
		[prefMap, upsertPreference]
	);

	const handleResetToDefault = useCallback(
		async (groupKey: string) => {
			try {
				await deletePreference(groupKey);
			} catch (e) {
				console.error("Failed to reset preference:", e);
			}
		},
		[deletePreference]
	);

	if (!isActive) {
		return null;
	}

	if (isLoadingGroups || isLoadingPrefs) {
		return (
			<div className="flex items-center justify-center py-8">
				<span className="text-sm text-gray-400">{tp("loading")}</span>
			</div>
		);
	}

	return (
		<div className="bg-white rounded-xl border border-border-light p-6">
			<h3 className="text-base font-semibold text-gray-900 mb-1">{tp("title")}</h3>
			<p className="text-xs text-gray-500 mb-4">{tp("subtitle")}</p>

			{/* Table header */}
			<div className="grid grid-cols-[1fr_80px_80px_80px_120px_40px] gap-2 px-3 py-2 bg-brand-white border border-b-transparent border-border-light rounded-t-lg text-xs font-semibold text-text-meta uppercase">
				<span>{tp("headers.group")}</span>
				<span className="text-center">{tp("headers.inApp")}</span>
				<span className="text-center">{tp("headers.email")}</span>
				<span className="text-center">{tp("headers.digest")}</span>
				<span className="text-center">{tp("headers.frequency")}</span>
				<span />
			</div>

			{/* Group rows */}
			<div className="border border-border-light rounded-b-lg">
				{groups?.map((group) => {
					const pref = prefMap[group.key];
					const appEnabled = pref?.appEnabled ?? DEFAULT_PREF.appEnabled;
					const emailEnabled = pref?.emailEnabled ?? DEFAULT_PREF.emailEnabled;
					const digestEnabled = pref?.digestEnabled ?? DEFAULT_PREF.digestEnabled;
					const frequency = pref?.frequency ?? DEFAULT_PREF.frequency;

					return (
						<div key={group.key} className="border-b border-border-light last:border-b-0">
							<div className="grid grid-cols-[1fr_80px_80px_80px_120px_40px] gap-2 items-center px-3 py-2.5">
								<div>
									<span className="text-sm text-gray-900">{group.label}</span>
									<p className="text-xs text-gray-400 mt-0.5">{group.description}</p>
								</div>

								<div className="flex justify-center">
									<Toggle
										isChecked={appEnabled}
										onChange={() => handleToggle(group.key, "appEnabled", appEnabled)}
									/>
								</div>

								<div className="flex justify-center">
									<Toggle
										isChecked={emailEnabled}
										onChange={() => handleToggle(group.key, "emailEnabled", emailEnabled)}
									/>
								</div>

								<div className="flex justify-center">
									<Toggle
										isChecked={digestEnabled}
										onChange={() => handleToggle(group.key, "digestEnabled", digestEnabled)}
									/>
								</div>

								<div className="flex justify-center">
									<select
										className="text-xs border border-border-light rounded-lg px-2 py-1 bg-white text-gray-700 cursor-pointer"
										value={frequency}
										onChange={(e) =>
											handleFrequencyChange(group.key, e.target.value as NotificationFrequency)
										}
									>
										{Object.values(NotificationFrequency).map((freq) => (
											<option key={freq} value={freq}>
												{freq.charAt(0) + freq.slice(1).toLowerCase()}
											</option>
										))}
									</select>
								</div>

								<div className="flex justify-center">
									{pref && (
										<Tooltip content={<>{tp("resetToDefaults")}</>}>
											<button
												className="text-gray-400 hover:text-status-error cursor-pointer transition-colors"
												onClick={() => handleResetToDefault(group.key)}
											>
												<Icon icon={faTrash} size="sm" />
											</button>
										</Tooltip>
									)}
								</div>
							</div>

							{group.subToggles?.length ? (
								<div className="px-6 pb-3 pt-1 flex flex-col gap-1 bg-brand-white/40">
									{group.subToggles.map((sub) => {
										const subValue =
											(pref?.thresholds?.[sub.key] as boolean | undefined) ?? sub.default;

										return (
											<div key={sub.key} className="flex items-center justify-between gap-3 py-1">
												<div>
													<span className="text-xs text-gray-700">{sub.label}</span>
													<p className="text-[11px] text-gray-400 mt-0.5">{sub.description}</p>
												</div>
												<Toggle
													testId={`subtoggle-${group.key}-${sub.key}`}
													isChecked={subValue}
													onChange={() => handleSubToggle(group, sub.key, subValue)}
												/>
											</div>
										);
									})}

									{group.colleagueScope ? (
										<ReplyColleagueScopePicker
											currentUserId={user?.id}
											group={group}
											thresholds={pref?.thresholds}
											onChange={(patch) => handleThresholdsChange(group, patch)}
										/>
									) : null}
								</div>
							) : null}
						</div>
					);
				})}
			</div>
		</div>
	);
};
